<template>
  <div class="calendar-page">
    <NavBar />

    <div class="calendar-header">
      <div class="container">
        <h1 class="page-title">
          {{ $t('calendar.title') }}
        </h1>
        <p class="page-subtitle">
          {{ $t('calendar.subtitle') }}
        </p>
      </div>
    </div>

    <div class="calendar-content">
      <div class="container">
        <!-- 筛选和视图控制 -->
        <div class="calendar-controls">
          <div class="view-toggle">
            <van-button
              :type="viewMode === 'list' ? 'primary' : 'default'"
              size="small"
              @click="viewMode = 'list'"
            >
              {{ $t('calendar.listView') }}
            </van-button>
            <van-button
              :type="viewMode === 'month' ? 'primary' : 'default'"
              size="small"
              @click="viewMode = 'month'"
            >
              {{ $t('calendar.monthView') }}
            </van-button>
          </div>

          <!--          <div class="filter-controls">-->
          <!--            <van-dropdown-menu>-->
          <!--              <van-dropdown-item-->
          <!--                v-model="currentFilter"-->
          <!--                :options="filterOptions"-->
          <!--                @change="handleFilterChange"-->
          <!--              />-->
          <!--            </van-dropdown-menu>-->
          <!--          </div>-->
        </div>

        <div class="tags-container">
          <div
            v-for="tag in tagList"
            :key="tag.id"
            class="tag"
            :class="{ 'active': selectedTags.indexOf(tag.name) !== -1 }"
            type="primary"
            size="medium"
            @click="handleSelectedTag(tag)"
          >
            {{ tag.name }}
          </div>
        </div>

        <!-- 月视图 -->
        <div
          v-if="viewMode === 'month'"
          class="month-view"
        >
          <div class="calendar-nav">
            <van-button
              icon="arrow-left"
              size="small"
              @click="prevMonth"
            />
            <h2 class="current-month">
              {{ currentMonthText }}
            </h2>
            <van-button
              icon="arrow"
              size="small"
              @click="nextMonth"
            />
          </div>

          <div class="calendar-grid">
            <div class="weekdays">
              <div
                v-for="day in weekdays"
                :key="day"
                class="weekday"
              >
                {{ day }}
              </div>
            </div>

            <div class="days-grid">
              <div
                v-for="day in calendarDays"
                :key="day.id"
                class="calendar-day"
                :class="{
                  'current-month': day.isCurrentMonth,
                  'today': day.isToday,
                  'has-events': day.events.length > 0,
                  'selected': selectedDate === day.date
                }"
                @click="selectDate(day)"
              >
                <div class="day-number">
                  {{ day.day }}
                </div>
                <div class="day-events">
                  <span
                    v-for="event in day.events.slice(0, 2)"
                    :key="event.id"
                    class="event-dot"
                    :style="{ backgroundColor: getEventColor(event.activityType) }"
                  />
                  <span
                    v-if="day.events.length > 2"
                    class="more-events"
                  >
                    +{{ day.events.length - 2 }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 列表视图 -->
        <div
          v-if="viewMode === 'list'"
          class="list-view"
        >
          <div class="events-list" v-if="events && events.length > 0">
            <div
              v-for="event in events"
              :key="event.id"
              class="event-item"
              :class="`event-type-${event.type}`"
            >
              <div class="event-date">
                <div class="event-day">
                  {{ formatEventDay(event.startTime) }}
                </div>
                <div class="event-month">
                  {{ formatEventMonth(event.startTime) }}
                </div>
              </div>

              <div class="event-content">
                <div class="event-header">
                  <span
                    class="event-type"
                    :style="{ color: getEventColor(event.activityType) }"
                  >
                    {{ getEventTypeText(event.activityType) }}
                  </span>
                  <span class="event-time">{{ getEventDateText(event) }}</span>
                </div>

                <h3 class="event-title">
                  {{ event.title }}
                </h3>
                <p class="event-description">
                  {{ event.description }}
                </p>

                <div class="event-footer">
                  <!--                  <span class="event-brand">{{ event.brand }}</span>-->
                  <div class="event-tags">
                    <van-tag
                      v-for="tag in event.tags"
                      :key="tag"
                      class="tag"
                      type="primary"
                    >
                      {{ tag }}
                    </van-tag>
                  </div>
                  <van-button
                    size="mini"
                    plain
                    @click="handleEventClick(event)"
                  >
                    {{ $t('calendar.viewDetails') }}
                  </van-button>
                </div>
              </div>
            </div>
          </div>
          <div v-else>
            <div class="no-events">
<!--              {{ $t('calendar.noEvents') }}-->
              <van-empty :description="$t('calendar.noEvents')"/>
            </div>
          </div>
        </div>

        <!-- 选中日期的活动详情 -->
        <van-popup
          v-model:show="showEventDetail"
          position="bottom"
          round
          :style="{ height: '60%' }"
        >
          <div class="event-detail">
            <div class="detail-header">
              <h3>{{ selectedEvent?.title }}</h3>
              <van-icon
                name="cross"
                @click="showEventDetail = false"
              />
            </div>

            <div class="detail-content">
              <div class="detail-meta">
                <div class="meta-item">
                  <van-icon name="clock" />
                  <span>{{ getEventDateText(selectedEvent) }}</span>
                </div>
                <div class="meta-item">
                  <van-icon name="label" />
                  <span :style="{ color: getEventColor(selectedEvent?.activityType) }">
                    {{ getEventTypeText(selectedEvent?.activityType) }}
                  </span>
                </div>
                <div
                  v-if="selectedEvent?.brand"
                  class="meta-item"
                >
                  <van-icon name="shop" />
                  <span>{{ selectedEvent?.brand }}</span>
                </div>
              </div>

              <div class="detail-description">
                <h4>{{ $t('calendar.eventDescription') }}</h4>
                <p>{{ selectedEvent?.description }}</p>
              </div>

              <div v-html="selectedEvent?.content" />

              <div
                v-if="selectedEvent?.link"
                class="detail-actions"
              >
                <van-button
                  type="primary"
                  block
                  @click="handleEventAction(selectedEvent)"
                >
                  {{ $t('calendar.participate') }}
                </van-button>
              </div>
            </div>
          </div>
        </van-popup>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { ref, computed, onMounted, watchEffect, reactive, watch } from 'vue'
import { useRouter } from 'vue-router'
import { showToast } from 'vant'
import NavBar from '@/components/NavBar.vue'
import { CalendarEvent, EventRequest, EventTag, queryCalendarEventList, queryEventTagList } from '@/api/calendar'
import { useI18n } from 'vue-i18n'
import { handleHtmlContent } from '@/utils/utils'
import { getUserLang } from '@/storage/user'
// import handle
import moment from 'moment'
import { OptionItem } from '@/api/base'
export default {
  name: 'Calendar',
  components: {
    NavBar
  },
  setup () {
    const router = useRouter()
    const { t } = useI18n()

    // 事件类型定义
    // type EventType = 'conference' | 'exhibition' | 'webinar' | 'promotion' | 'deadline'
    type EventType = 1 | 2 | 99

    const { locale } = useI18n()
    watch(locale, () => {
      getEventTagList()
      getEventList()
    })

    // 响应式数据
    const viewMode = ref<'month' | 'list'>('list')
    const currentFilter = ref('all')
    const currentDate = ref(new Date())
    const selectedDate = ref('')
    const selectedEvent = ref<CalendarEvent | null>(null)
    const showEventDetail = ref(false)

    const tagList = ref<EventTag[]>([])
    // const selectedTags = ref('')
    const selectedTags = ref<string[]>([])
    watch(() => selectedTags.value, (newValue) => {
      params.tags = newValue.join(',')
      getEventList()
    })
    // 筛选选项
    const filterOptions = ref<OptionItem[]>([])
    // [
    // { text: '所有活动', value: 'all' },
    // { text: '会议', value: 'conference' },
    // { text: '展览', value: 'exhibition' },
    // { text: '线上研讨会', value: 'webinar' },
    // { text: '促销活动', value: 'promotion' },
    // { text: '截止日期', value: 'deadline' }
    // ]
    function getEventTagList () {
      const param = {
        page: 1,
        size: 99
      }
      queryEventTagList(param).then(res => {
        selectedTags.value = []
        tagList.value = res.list
        // tagList.value.forEach(tag => {
        //   filterOptions.value.push({
        //     text: tag.name,
        //     value: tag.name
        //   })
        // })
      })
    }
    function handleSelectedTag (tag: EventTag) {
      if (selectedTags.value.includes(tag.name)) {
        // selectedTags.value = selectedTags.value.filter(t => t !== tag)
        const index = selectedTags.value.indexOf(tag.name)
        selectedTags.value.splice(index, 1)
      } else {
        selectedTags.value.push(tag.name)
      }
      params.tags = selectedTags.value.join(',')
      getEventList()
      // console.log('new selectedTags', selectedTags.value)
    }
    //
    const events = ref<CalendarEvent[]>([])
    const params:EventRequest = reactive({
      order: 'desc',
      orderBy: 'start_time',
      tags: ''
    })
    function getEventList () {
      queryCalendarEventList(params).then(res => {
        events.value = res.list
      })
    }

    // 星期几的显示文本
    // const weekdays = ref(['日', '一', '二', '三', '四', '五', '六'])
    const weekdays = ref<string[]>([])
    watchEffect(() => {
      weekdays.value = [
        t('calendar.sunday'),
        t('calendar.monday'),
        t('calendar.tuesday'),
        t('calendar.wednesday'),
        t('calendar.thursday'),
        t('calendar.friday'),
        t('calendar.saturday')
      ]
    })

    // 计算属性
    const currentMonthText = computed(() => {
      const lang = getUserLang()
      return currentDate.value.toLocaleDateString(lang, {
        year: 'numeric',
        month: 'long'
      })
    })

    const filteredEvents = computed(() => {
      // if (currentFilter.value === 'all') {
      //   return events.value
      // }
      // return events.value.filter(event => event.activityType === currentFilter.value)
    })

    const calendarDays = computed(() => {
      const year = currentDate.value.getFullYear()
      const month = currentDate.value.getMonth()

      // 当月第一天
      const firstDay = new Date(year, month, 1)
      // 当月最后一天
      const lastDay = new Date(year, month + 1, 0)
      // 当月第一天是星期几（0-6，0代表周日）
      const firstDayOfWeek = firstDay.getDay()
      // 上个月最后一天
      const prevMonthLastDay = new Date(year, month, 0).getDate()

      const days = []
      const today = new Date()
      const todayStr = today.toISOString().split('T')[0]

      // 添加上个月的日期
      for (let i = firstDayOfWeek - 1; i >= 0; i--) {
        const date = new Date(year, month - 1, prevMonthLastDay - i)
        days.push({
          id: `prev-${i}`,
          date: date.toISOString().split('T')[0],
          day: date.getDate(),
          isCurrentMonth: false,
          isToday: false,
          events: getEventsForDate(date)
        })
      }

      // 添加当月的日期
      for (let i = 1; i <= lastDay.getDate(); i++) {
        const date = new Date(year, month, i)
        const dateStr = date.toISOString().split('T')[0]
        days.push({
          id: `current-${i}`,
          date: dateStr,
          day: i,
          isCurrentMonth: true,
          isToday: dateStr === todayStr,
          events: getEventsForDate(date)
        })
      }

      // 添加下个月的日期（补全日历）
      const nextMonthDays = 42 - days.length // 6行×7天
      for (let i = 1; i <= nextMonthDays; i++) {
        const date = new Date(year, month + 1, i)
        days.push({
          id: `next-${i}`,
          date: date.toISOString().split('T')[0],
          day: i,
          isCurrentMonth: false,
          isToday: false,
          events: getEventsForDate(date)
        })
      }

      return days
    })

    // 方法
    const getEventsForDate = (date: Date) => {
      const dateStr = date.toISOString().split('T')[0]
      return events.value.filter(event => moment(event.startTime).format('YYYY-MM-DD') === dateStr)
      // return events.value.filter(event => event.startTime === dateStr)
      // new Date(event.startTime)
    }

    const getEventColor = (type?: EventType) => {
      // const colors = {
      //   conference: '#3498db',
      //   exhibition: '#e74c3c',
      //   webinar: '#9b59b6',
      //   promotion: '#f39c12',
      //   deadline: '#e67e22'
      // }
      const colors = {
        1: '#3498db',
        2: '#e74c3c',
        3: '#9b59b6',
        4: '#f39c12',
        99: '#e67e22'
      }
      return type ? colors[type] : '#95a5a6'
    }

    const getEventTypeText = (type?: EventType) => {
      // console.log('111', type)
      // const texts = {
      //   conference: '会议',
      //   exhibition: '展览',
      //   webinar: '线上研讨会',
      //   promotion: '促销活动',
      //   deadline: '截止日期'
      // }
      const texts = {
        1: t('calendar.tradeFair'), // '订货会',
        2: t('calendar.showRoom'),
        3: t('calendar.exhibition'),
        99: t('calendar.event')
        // 3: '线上研讨会',
        // 4: '促销活动',
        // 5: '截止日期'
      }
      return type ? texts[type] : t('calendar.event')
      // return '活动'
    }
    const formatEventDay = (dateStr: string) => {
      const date = new Date(dateStr)
      // const lang = getUserLang()
      // return date.toLocaleDateString(lang, {
      //   weekday: 'short'
      // })
      return date.getDate().toString()
    }
    const formatEventMonth = (dateStr: string) => {
      const date = new Date(dateStr)
      const lang = getUserLang()
      return date.toLocaleDateString(lang, {
        month: 'long'
      })
      // return (date.getMonth() + 1) + '月'
    }

    const formatEventTime = (dateStr: string) => {
      const date = new Date(dateStr)
      return moment(date).format('HH:mm')
      // return (date.getHours()) + ''
    }

    const formatEventDate = (dateStr: string) => {
      const date = new Date(dateStr)
      return moment(date).format('YYYY-MM-DD')
      // return (date.getHours()) + ''
    }
    const getEventDateText = (event: CalendarEvent) => {
      const startDate = new Date(event.startTime)
      const endDate = new Date(event.endTime)
      const startDateText = moment(startDate).format('YYYY-MM-DD')
      const endDateText = moment(endDate).format('YYYY-MM-DD')
      if (startDateText === endDateText) {
        return `${startDateText} ${moment(startDate).format('HH:mm')}~${moment(endDate).format('HH:mm')}`
      } else {
        return `${startDateText} - ${endDateText}`
      }
    }

    const prevMonth = () => {
      currentDate.value = new Date(
        currentDate.value.getFullYear(),
        currentDate.value.getMonth() - 1,
        1
      )
    }

    const nextMonth = () => {
      currentDate.value = new Date(
        currentDate.value.getFullYear(),
        currentDate.value.getMonth() + 1,
        1
      )
    }

    const selectDate = (day: any) => {
      selectedDate.value = day.date
      if (day.events.length > 0) {
        selectedEvent.value = day.events[0]
        showEventDetail.value = true
      }
    }

    const handleFilterChange = () => {
      // 筛选变化处理
      console.log('当前筛选:', currentFilter.value)
    }

    const handleEventClick = (event: CalendarEvent) => {
      selectedEvent.value = event
      selectedEvent.value.content = handleHtmlContent(event?.content)
      showEventDetail.value = true
    }

    const handleEventAction = (event: CalendarEvent) => {
      if (event.link) {
        if (event.link.startsWith('/')) {
          router.push(event.link)
        } else {
          window.open(event.link, '_blank')
        }
      } else {
        showToast('活动详情加载中...')
      }
    }

    onMounted(() => {
      getEventTagList()
      getEventList()
      // 设置默认选中今天
      const today = new Date()
      selectedDate.value = today.toISOString().split('T')[0]
    })
    return {
      viewMode,
      tagList,
      selectedTags,
      handleSelectedTag,
      events,
      currentMonthText,
      calendarDays,
      filterOptions,
      currentFilter,
      selectedDate,
      filteredEvents,
      weekdays,
      getEventColor,
      getEventTypeText,
      formatEventDay,
      formatEventMonth,
      formatEventTime,
      formatEventDate,
      getEventDateText,
      prevMonth,
      nextMonth,
      selectDate,
      handleFilterChange,
      handleEventClick,
      handleEventAction,
      showEventDetail,
      selectedEvent

    }
  }
}

</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.calendar-page {
  min-height: 100vh;
  background-color: $light-gray;
}

.calendar-header {
  background: linear-gradient(135deg, $primary-blue 0%, darken($primary-blue, 10%) 100%);
  color: $white;
  padding: $spacing-xl 0;
  text-align: center;
}

.page-title {
  font-size: $font-size-xxl;
  font-weight: 700;
  margin-bottom: $spacing-sm;
}

.page-subtitle {
  font-size: $font-size-lg;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
}

.calendar-content {
  padding: $spacing-xl 0;
}

.calendar-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: $spacing-xl;
  background: $white;
  padding: $spacing-lg;
  border-radius: $border-radius-lg;
  box-shadow: $card-shadow;
}

.view-toggle {
  display: flex;
  gap: $spacing-sm;
}

.tags-container{
  display: flex;
  flex-wrap: wrap;
  margin-bottom: $spacing-xl;
  max-width: 98%;
  .tag{
    background: $light-gray;
    padding: $spacing-xs $spacing-sm;
    //border-radius: $border-radius-lg;
    color: $tag-color;
    cursor: pointer;
    font-size: $font-size-xs;
    border: 1px solid $tag-color;
    margin-left: $spacing-md;
    margin-bottom: $spacing-md;
    font-weight: 500;
    &.active{
      background: $tag-color;
      color: $white;
    }
    //&:hover{
    //  background: $light-gray;
    //  color: $text-light;
    //}
    @media (max-width: $breakpoint-sm) {
      padding: $spacing-xs $spacing-xs;
    }
  }
}
.month-view {
  background: $white;
  border-radius: $border-radius-lg;
  padding: $spacing-lg;
  box-shadow: $card-shadow;
}

.calendar-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: $spacing-lg;

  .current-month {
    font-size: $font-size-lg;
    font-weight: 600;
    color: $primary-blue;
    margin: 0;
  }
}

.calendar-grid {
  .weekdays {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 1px;
    margin-bottom: $spacing-md;

    .weekday {
      text-align: center;
      font-weight: 600;
      color: $text-light;
      padding: $spacing-sm 0;
      font-size: $font-size-sm;
    }
  }

  .days-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 1px;
    background-color: rgba($light-gray, 0.5);
  }
}

.calendar-day {
  background: $white;
  min-height: 80px;
  padding: $spacing-xs;
  cursor: pointer;
  transition: all $transition-fast;
  border: 2px solid transparent;

  &:hover {
    background: lighten($light-gray, 2%);
  }

  &.current-month {
    .day-number {
      color: $primary-blue;
    }
  }

  &:not(.current-month) {
    .day-number {
      color: $text-light;
      opacity: 0.5;
    }
  }

  &.today {
    border-color: $accent-gold;

    .day-number {
      color: $accent-gold;
      font-weight: 700;
    }
  }

  &.has-events {
    background: lighten($accent-gold, 40%);
  }

  &.selected {
    border-color: $primary-blue;
    background: lighten($primary-blue, 45%);
  }

  .day-number {
    font-size: $font-size-sm;
    margin-bottom: $spacing-xs;
  }

  .day-events {
    display: flex;
    flex-wrap: wrap;
    gap: 2px;
    justify-content: center;
  }

  .event-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    display: inline-block;
  }

  .more-events {
    font-size: $font-size-xs;
    color: $text-light;
  }
}

.list-view {
  .events-list {
    display: flex;
    flex-direction: column;
    gap: $spacing-md;
  }
}

.event-item {
  display: flex;
  background: $white;
  border-radius: $border-radius-lg;
  padding: $spacing-lg;
  box-shadow: $card-shadow;
  transition: transform $transition-normal;

  &:hover {
    transform: translateY(-2px);
  }

  .event-date {
    flex-shrink: 0;
    width: 60px;
    text-align: center;
    margin-right: $spacing-lg;

    .event-day {
      font-size: $font-size-xl;
      font-weight: 700;
      color: $primary-blue;
      line-height: 1;
    }

    .event-month {
      font-size: $font-size-sm;
      color: $text-light;
      margin-top: 2px;
    }
  }

  .event-content {
    flex: 1;
    text-align: left;
  }

  .event-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: $spacing-sm;
  }

  .event-type {
    font-size: $font-size-xs;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .event-time {
    font-size: $font-size-xs;
    color: $text-light;
  }

  .event-title {
    font-size: $font-size-lg;
    font-weight: 600;
    margin-bottom: $spacing-xs;
    color: $primary-blue;
  }

  .event-description {
    font-size: $font-size-sm;
    color: $text-light;
    line-height: 1.4;
    margin-bottom: $spacing-md;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .event-footer {
    display: flex;
    justify-content: space-between;
    //align-items: center;
    .event-tags{
      display: flex;
      flex-wrap: wrap;
      .tag{
        margin-right: 15px;
        padding: 3px 10px;
        margin-bottom: 10px;
      }
    }

    .event-brand {
      font-size: $font-size-sm;
      color: $text-light;
      font-weight: 500;
    }
  }
}

.event-detail {
  padding: $spacing-xl;

  .detail-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: $spacing-lg;
    border-bottom: 1px solid $light-gray;
    padding-bottom: $spacing-md;

    h3 {
      margin: 0;
      color: $primary-blue;
      font-size: $font-size-lg;
    }

    .van-icon {
      font-size: $font-size-lg;
      color: $text-light;
      cursor: pointer;
    }
  }

  .detail-meta {
    margin-bottom: $spacing-lg;

    .meta-item {
      display: flex;
      align-items: center;
      margin-bottom: $spacing-sm;
      font-size: $font-size-sm;

      .van-icon {
        margin-right: $spacing-sm;
        color: $text-light;
      }
    }
  }

  .detail-description {
    margin-bottom: $spacing-xl;

    h4 {
      font-size: $font-size-md;
      margin-bottom: $spacing-sm;
      color: $primary-blue;
    }

    p {
      line-height: 1.6;
      color: $text-dark;
    }
  }
}

// 1080×1920 设备适配
@media (min-width: 1080px) and (min-height: 1920px) {
  .calendar-header {
    padding: $spacing-xxl 0;
  }

  .page-title {
    font-size: 2.8rem;
  }

  .page-subtitle {
    font-size: 1.4rem;
  }

  .calendar-content {
    padding: $spacing-xxl 0;
  }

  .calendar-controls {
    padding: $spacing-xl;
    margin-bottom: $spacing-xxl;
  }

  .month-view {
    padding: $spacing-xl;
  }

  .calendar-day {
    min-height: 100px;
    padding: $spacing-sm;

    .day-number {
      font-size: $font-size-md;
    }

    .event-dot {
      width: 8px;
      height: 8px;
    }
  }

  .event-item {
    padding: $spacing-xl;

    .event-date {
      width: 80px;

      .event-day {
        font-size: $font-size-xxl;
      }

      .event-month {
        font-size: $font-size-md;
      }
    }

    .event-title {
      font-size: $font-size-xl;
    }

    .event-description {
      font-size: $font-size-md;
    }
  }

  .event-detail {
    padding: $spacing-xxl;

    .detail-header h3 {
      font-size: $font-size-xl;
    }
  }
}

// 平板设备适配
@media (max-width: 768px) {
  .calendar-controls {
    flex-direction: column;
    gap: $spacing-md;
    align-items: stretch;
  }

  .view-toggle {
    justify-content: center;
  }

  .calendar-day {
    min-height: 60px;

    .day-number {
      font-size: $font-size-xs;
    }
  }

  .event-item {
    flex-direction: column;

    .event-date {
      width: 100%;
      margin-right: 0;
      margin-bottom: $spacing-md;
      display: flex;
      align-items: center;
      gap: $spacing-sm;

      .event-day {
        font-size: $font-size-lg;
      }
    }
  }
}

// 移动端适配
@media (max-width: 480px) {
  .calendar-header {
    padding: $spacing-lg 0;
  }

  .page-title {
    font-size: $font-size-xl;
  }

  .page-subtitle {
    font-size: $font-size-md;
  }

  .calendar-content {
    padding: $spacing-lg 0;
  }

  .month-view {
    padding: $spacing-md;
  }

  .calendar-day {
    min-height: 50px;
    padding: 4px;

    .day-number {
      font-size: $font-size-xs;
    }
  }
}
</style>
